import { type Hex, TerrainType } from "@/core/types/battle.types";
import { MapConfig } from "@/core/types/global.types";
import { mulberry32, hashSeed } from "@/core/utils/battle.utils";
import { getOffsetNeighbors, coordsToId, getAxialNeighbors, getMapRows } from "@/core/utils/hex.utils";


export class HexMapGenerator {
  private map: Map<string, Hex>;
  private rng: () => number;
  private size: number;
  private rows: number;
  private hillChance: number;
  private obstacleChance: number;

  constructor(options: MapConfig) {
    const { size, seed, hillChance, obstacleChance } = options;
    
    this.size = size;
    this.hillChance = hillChance;
    this.obstacleChance = obstacleChance;
    // Calculate row limit based on logic provided
    this.rows = getMapRows(size);
    
    // Initialize PRNG
    this.rng = mulberry32(hashSeed(seed));
    this.map = new Map<string, Hex>();
  }

  /**
   * Main public method to generate the map
   */
  public generate(): Map<string, Hex> {
    this.initializeGrid();
    this.applyTerrainFeatures();
    // this.promoteCliffs();

    return this.map;
  }

  // =========================================
  // Core Generation Steps
  // =========================================

  private initializeGrid(): void {
    for (let q = 0; q < this.size; q++) {
      // Offset for Axial Rectangle shape
      const qOffset = Math.floor(q / 2);

      for (let r = -qOffset; r < this.rows - qOffset; r++) {
        const id = coordsToId({q, r});

        this.map.set(id, {
          id: id,
          q: q,
          r: r,
          x: 0, // Assuming layout calculations happen elsewhere or x/y are 0 for now
          y: 0,
          terrain: TerrainType.Plains,
          isCliff: false,
          isObstacle: false,
        });
      }
    }
  }

  private applyTerrainFeatures(): void {
    // ПЕРШИЙ ПРОХІД: Рощуємо пагорби
    for (const hex of this.map.values()) {
      if (
        hex.terrain === TerrainType.Plains &&
        this.rng() < this.calculateHillSeedChance(hex.q)
      ) {
        this.growHillPatch(hex.q, hex.r);
      }
    }

    // ДРУГИЙ ПРОХІД: Ставимо об'єкти тільки там, де залишився Flat
    for (const hex of this.map.values()) {
      this.maybePlaceObstacle(hex);
    }
  }

  private promoteCliffs(): void {
    for (const hex of this.map.values()) {
      if (hex.terrain !== TerrainType.Hill) continue;

      const neighbors = getAxialNeighbors(hex.q, hex.r);
      let hillNeighbors = 0;

      for (const nCoord of neighbors) {
        const n = this.getHex(nCoord.q, nCoord.r);
        if (n?.terrain === TerrainType.Hill) hillNeighbors++;
      }

      // If surrounded by hills, small chance to become a cliff
      if (hillNeighbors >= 3 && this.rng() < 0.15) {
        hex.terrain = TerrainType.Cliff;
        hex.isCliff = true;
      }
    }
  }

  // =========================================
  // Terrain Logic Helpers
  // =========================================

  private maybePlaceObstacle(hex: Hex): void {
    if (this.obstacleChance > 0 && !hex.isCliff && this.rng() < (this.obstacleChance / 100)) {
      // hex.terrain = TerrainType.Obstacle;

      hex.isObstacle = true;
    }
  }

  private calculateHillSeedChance(q: number): number {
    
    const centerQ = (this.size - 1) / 2;
    const dx = Math.abs(q - centerQ) / centerQ;
    
    // HILL_CHANCE — базова ймовірність (наприклад, 0.1 або 10%)
    // (HILL_CHANCE + 0.02) визначає силу приросту шансу до країв
    const hillChance = this.hillChance > 0 ? (this.hillChance/100) + dx * 0.015 : 0;

    return hillChance; 
}

  private growHillPatch(startQ: number, startR: number): void {
    const stack = [{ q: startQ, r: startR, strength: 1 }];

    while (stack.length) {
      const cur = stack.pop()!;
      const hex = this.getHex(cur.q, cur.r);

      // If hex missing or already modified (Obstacle/Hill), skip
      if (!hex || hex.terrain !== TerrainType.Plains) continue;

      hex.terrain = TerrainType.Hill;

      const neighborsCoords = getAxialNeighbors(cur.q, cur.r);

      for (const coords of neighborsCoords) {
        const neighbor = this.getHex(coords.q, coords.r);

        if (neighbor && neighbor.terrain === TerrainType.Plains) {
          // Decay logic
          if (this.rng() < 0.25 * cur.strength) {
            stack.push({
              q: coords.q,
              r: coords.r,
              strength: cur.strength * 0.4,
            });
          }
        }
      }
    }
  }

  private getHex(q: number, r: number): Hex | undefined {
    return this.map.get(coordsToId({q, r}));
  }
}
